import React, { useEffect, useState } from 'react';
import { saasApi } from '../../utils/saasApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import DrawerShell from '../../components/DrawerShell';
import { Plus, Check, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';

function PlanCreateDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: '', name: '', price_monthly: 0, currency: 'usd', features: '', limits_users: 5, limits_patients: 100,
  });

  const submit = async () => {
    if (!form.code || !form.name) {
      toast.error('Code and name are required'); return;
    }
    setSaving(true);
    try {
      await saasApi.createPlan({
        code: form.code, name: form.name,
        price_monthly: parseFloat(form.price_monthly || 0),
        currency: form.currency,
        features: form.features.split(/\n+/).map((s) => s.trim()).filter(Boolean),
        limits: {
          users: parseInt(form.limits_users, 10) || -1,
          patients: parseInt(form.limits_patients, 10) || -1,
        },
        is_active: true,
      });
      toast.success(`Plan ${form.code} created`);
      setOpen(false);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
    setSaving(false);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="sa-new-plan-btn">
        <Plus className="h-4 w-4 mr-2" /> New plan
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Pricing Tier"
        title="Create a new plan"
        subtitle="Sets price, feature list and tenant-level limits"
        accent="#d97706"
        icon={<Package className="h-5 w-5" />}
        width="md"
        testId="sa-plan-create-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="sa-plan-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create plan
            </Button>
          </>
        }
      >
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Code *</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })}
              placeholder="growth" data-testid="sa-plan-code" />
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Growth" data-testid="sa-plan-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Price / month (USD)</Label>
            <Input type="number" value={form.price_monthly}
              onChange={(e) => setForm({ ...form, price_monthly: e.target.value })}
              data-testid="sa-plan-price" />
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Input value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toLowerCase() })}
              data-testid="sa-plan-currency" />
          </div>
          <div className="space-y-1.5">
            <Label>User limit (-1 = unlimited)</Label>
            <Input type="number" value={form.limits_users}
              onChange={(e) => setForm({ ...form, limits_users: e.target.value })}
              data-testid="sa-plan-limit-users" />
          </div>
          <div className="space-y-1.5">
            <Label>Patient limit (-1 = unlimited)</Label>
            <Input type="number" value={form.limits_patients}
              onChange={(e) => setForm({ ...form, limits_patients: e.target.value })}
              data-testid="sa-plan-limit-patients" />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Features (one per line)</Label>
            <textarea
              className="w-full min-h-[110px] rounded-md border border-zinc-200 p-2 text-sm"
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              placeholder="Voice dictation&#10;Email support"
              data-testid="sa-plan-features"
            />
          </div>
        </div>
      </DrawerShell>
    </>
  );
}

export default function PlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try { setPlans(await saasApi.listPlans()); } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const toggleActive = async (p) => {
    try {
      await saasApi.updatePlan(p.id, { is_active: !p.is_active });
      refresh();
    } catch (_) { toast.error('Failed'); }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-bold text-zinc-900"
            style={{ fontFamily: 'Manrope' }}
            data-testid="sa-plans-title"
          >
            Plans
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Define the pricing tiers your tenants can subscribe to.
          </p>
        </div>
        <PlanCreateDialog onDone={refresh} />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : plans.map((p) => (
          <div
            key={p.id}
            className="bg-white border rounded-xl p-5 flex flex-col"
            data-testid={`sa-plan-card-${p.code}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-zinc-500">{p.code}</div>
                <div className="text-xl font-bold text-zinc-900 mt-0.5">{p.name}</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-zinc-500">Active</span>
                <Switch checked={!!p.is_active}
                  onCheckedChange={() => toggleActive(p)}
                  data-testid={`sa-plan-active-${p.code}`} />
              </div>
            </div>
            <div className="mt-3 text-3xl font-bold text-zinc-900">
              ${p.price_monthly}
              <span className="text-sm font-normal text-zinc-500"> /mo</span>
            </div>
            <ul className="mt-4 space-y-1.5 text-sm text-zinc-700 flex-1">
              {(p.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-[11px] text-zinc-500 border-t pt-3">
              Users limit: {p.limits?.users === -1 ? 'Unlimited' : p.limits?.users ?? '—'} ·{' '}
              Patients: {p.limits?.patients === -1 ? 'Unlimited' : p.limits?.patients ?? '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
